import type { Business } from "@/lib/types";
import { SOKO_GRID_EMAIL, SOKO_GRID_EMAIL_URL } from "@/src/config/contact";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

type Props = {
  business: Business;
  publicUrl: string;
  visible: boolean;
  onClose: () => void;
};

const REPORT_REASONS = [
  "Wrong information",
  "Fraud or scam",
  "Offensive content",
  "Fake business",
  "Wrong location",
  "Duplicate business",
  "Other",
];

const FALLBACK_MESSAGE = `Please email ${SOKO_GRID_EMAIL} with the business name and issue.`;

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

function getBusinessField(business: Business, key: string) {
  const record = business as unknown as Record<string, unknown>;

  return cleanText(record[key]);
}

function getBusinessName(business: Business) {
  return getBusinessField(business, "name") || "Business";
}

function getBusinessSlug(business: Business) {
  return getBusinessField(business, "slug");
}

function getBusinessId(business: Business, publicUrl: string) {
  return (
    getBusinessField(business, "id") ||
    getBusinessField(business, "businessId") ||
    getBusinessSlug(business) ||
    publicUrl
  );
}

function showFallbackMessage() {
  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof window.alert === "function"
  ) {
    window.alert(FALLBACK_MESSAGE);
    return;
  }

  Alert.alert("Email Us", FALLBACK_MESSAGE);
}

function buildMailtoUrl({
  business,
  publicUrl,
  reason,
  details,
}: {
  business: Business;
  publicUrl: string;
  reason: string;
  details: string;
}) {
  const businessName = getBusinessName(business);
  const slug = getBusinessSlug(business) || "Not available";
  const optionalDetails = cleanText(details) || "Not provided";

  const subject = `Report business: ${businessName}`;
  const body = [
    `Business name: ${businessName}`,
    `Business ID: ${getBusinessId(business, publicUrl)}`,
    `Business slug: ${slug}`,
    `Public URL: ${publicUrl}`,
    `Selected reason: ${reason}`,
    `Optional details: ${optionalDetails}`,
    `Date/time: ${new Date().toISOString()}`,
  ].join("\n");

  return `${SOKO_GRID_EMAIL_URL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
}

export default function BusinessReportModal({
  business,
  publicUrl,
  visible,
  onClose,
}: Props) {
  const [selectedReason, setSelectedReason] = useState(REPORT_REASONS[0]);
  const [details, setDetails] = useState("");
  const { height } = useWindowDimensions();
  const availableHeight = Math.max(280, height - 48);
  const modalMaxHeight = Math.min(availableHeight, 640);

  useEffect(() => {
    if (!visible) return;

    setSelectedReason(REPORT_REASONS[0]);
    setDetails("");
  }, [visible, business]);

  const mailtoUrl = useMemo(
    () =>
      buildMailtoUrl({
        business,
        publicUrl,
        reason: selectedReason,
        details,
      }),
    [business, details, publicUrl, selectedReason]
  );

  async function handleSendReport() {
    try {
      if (Platform.OS === "web") {
        if (typeof window === "undefined") {
          throw new Error("Email is not available in this browser.");
        }

        window.location.href = mailtoUrl;
        onClose();
        return;
      }

      await Linking.openURL(mailtoUrl);
      onClose();
    } catch (error) {
      console.log("Report email failed", error);
      showFallbackMessage();
    }
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={styles.keyboardView}
      >
        <Pressable
          accessibilityLabel="Close report business modal"
          onPress={onClose}
          style={StyleSheet.absoluteFill}
        />

        <View style={[styles.panel, { maxHeight: modalMaxHeight }]}>
          <Text style={styles.brand}>Soko Grid</Text>
          <Text style={styles.title}>Report Business</Text>
          <Text style={styles.intro}>
            Help Soko Grid keep listings accurate and safe.
          </Text>

          <ScrollView
            style={styles.modalScrollBody}
            contentContainerStyle={styles.modalScrollContent}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
          >
            <Text style={styles.label}>Reason</Text>

            <View style={styles.reasonList}>
              {REPORT_REASONS.map((reason) => {
                const selected = reason === selectedReason;

                return (
                  <Pressable
                    key={reason}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: selected }}
                    onPress={() => setSelectedReason(reason)}
                    style={[
                      styles.reasonOption,
                      selected ? styles.reasonOptionSelected : null,
                    ]}
                  >
                    <View
                      style={[
                        styles.radioOuter,
                        selected ? styles.radioOuterSelected : null,
                      ]}
                    >
                      {selected ? <View style={styles.radioInner} /> : null}
                    </View>

                    <Text
                      style={[
                        styles.reasonText,
                        selected ? styles.reasonTextSelected : null,
                      ]}
                    >
                      {reason}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <TextInput
              multiline
              onChangeText={setDetails}
              placeholder="Add more details\u2026"
              placeholderTextColor="#94a3b8"
              style={styles.detailsInput}
              textAlignVertical="top"
              value={details}
            />
          </ScrollView>

          <View style={styles.actions}>
            <Pressable onPress={onClose} style={styles.cancelButton}>
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>

            <Pressable onPress={handleSendReport} style={styles.sendButton}>
              <Text style={styles.sendText}>Send Report</Text>
            </Pressable>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
    backgroundColor: "rgba(15,23,42,0.48)",
  },

  panel: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: 18,
    shadowColor: "#000000",
    shadowOpacity: 0.18,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
  },

  brand: {
    alignSelf: "flex-start",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: "900",
    paddingHorizontal: 10,
    paddingVertical: 5,
    overflow: "hidden",
  },

  title: {
    marginTop: 12,
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "900",
    color: "#111827",
  },

  intro: {
    marginTop: 6,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#475569",
  },

  modalScrollBody: {
    flexShrink: 1,
    marginTop: 16,
  },

  modalScrollContent: {
    paddingBottom: 4,
  },

  label: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "900",
    color: "#334155",
  },

  reasonList: {
    marginTop: 8,
  },

  reasonOption: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    marginBottom: 8,
  },

  reasonOptionSelected: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
  },

  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: "#cbd5e1",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },

  radioOuterSelected: {
    borderColor: "#2563eb",
  },

  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#2563eb",
  },

  reasonText: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "800",
    color: "#334155",
  },

  reasonTextSelected: {
    color: "#1d4ed8",
  },

  detailsInput: {
    minHeight: 88,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#111827",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 16,
  },

  cancelButton: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    marginRight: 10,
  },

  cancelText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#334155",
  },

  sendButton: {
    minHeight: 42,
    borderRadius: 8,
    backgroundColor: "#2563eb",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },

  sendText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#ffffff",
  },
});
